import React, { lazy, Suspense, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { QueryClientProvider, QueryClient } from "react-query";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { PhantomWalletAdapter, SolflareWalletAdapter, CoinbaseWalletAdapter } from "@solana/wallet-adapter-wallets";
import "@solana/wallet-adapter-react-ui/styles.css";
import "./styles/wallet.css";
import MainLayout from "./layouts/MainLayout";
import ScreenLoading from "./components/ScreenLoading";
import NotFound from "./containers/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";

const Dashboard = lazy(() => import("./containers/Dashboard"));
const Configuration = lazy(() => import("./containers/Configuration"));
const Monitoring = lazy(() => import("./containers/Monitoring"));
const Referral = lazy(() => import("./containers/Referral"));
const BundleBuy = lazy(() => import("./containers/BundleBuy"));
const BundleMonitoring = lazy(() => import("./containers/BundleMonitoring"));
const Terminal = lazy(() => import("./containers/Terminal"));
const Landing = lazy(() => import("./containers/Landing"));
const FlashBuy = lazy(() => import("./containers/FlashBuy"));

const App = () => {
  const queryClient = new QueryClient();

  const endpoint = useMemo(() => process.env.REACT_APP_RPC_ENDPOINT, []);

  const wallets = useMemo(() => [new PhantomWalletAdapter(), new SolflareWalletAdapter(), new CoinbaseWalletAdapter()], []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <ToastContainer position="top-center" theme="colored" className="toast-position" />
              <Routes>
                <Route
                  path="/"
                  element={
                    <Suspense fallback={<ScreenLoading />}>
                      <Landing />
                    </Suspense>
                  }
                />

                <Route
                  path="/go"
                  element={
                    <Suspense fallback={<ScreenLoading />}>
                      <MainLayout />
                    </Suspense>
                  }
                >
                  <Route
                    index
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <Configuration />
                      </Suspense>
                    }
                  />

                  <Route
                    path="monitoring"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <ProtectedRoute>
                          <Monitoring />
                        </ProtectedRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="referral"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <Referral />
                      </Suspense>
                    }
                  />
                  <Route
                    path="dashboard"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="stealth-buy"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <BundleBuy />
                      </Suspense>
                    }
                  />
                  <Route
                    path="bundle-monitoring/:id"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <ProtectedRoute>
                          <BundleMonitoring />
                        </ProtectedRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="terminal"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <Terminal />
                      </Suspense>
                    }
                  />
                  <Route
                    path="flash-buy"
                    element={
                      <Suspense fallback={<ScreenLoading />}>
                        <FlashBuy />
                      </Suspense>
                    }
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </QueryClientProvider>
          </BrowserRouter>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export default App;
